import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * Toast Notification Component
 * Displays temporary messages for user actions
 * 
 * @param {string} type - Type of toast (success, error, info)
 * @param {string} message - Toast message
 * @param {function} onClose - Close handler
 * @param {number} duration - Auto-close duration in ms (0 = no auto-close)
 */
const Toast = ({ type = 'info', message, onClose, duration = 3000 }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const typeConfig = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-600'
    },
    error: {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-600'
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-600'
    }
  };

  const config = typeConfig?.[type] || typeConfig?.info;
  const IconComponent = config?.icon;

  return (
    <div className={`fixed top-4 right-4 z-[9999] max-w-md animate-slide-in-right`}>
      <div className={`${config?.bgColor} ${config?.borderColor} border rounded-lg shadow-lg p-4`}>
        <div className="flex items-start gap-3">
          <IconComponent className={`${config?.iconColor} w-5 h-5 mt-0.5 flex-shrink-0`} />
          <div className="flex-1">
            <p className={`${config?.textColor} text-sm font-medium`}>{message}</p>
          </div>
          <button
            onClick={onClose}
            className={`${config?.textColor} hover:opacity-70 transition-opacity`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;